// Mock data for classes - SAT V4 Zone
import type { Band, Class, Set as ClassSet, YearGroup } from '../../types';
import { mockPupils } from './pupils';
import { mockTeachers } from './teachers';

const yearGroups: YearGroup[] = [7, 8, 9, 10, 11];
const formGroupCodes = ['JW', 'FP', 'PH', 'SM', 'KL']; // Form tutor initials (matching pupils.ts)

// Generate form classes (registration/tutor groups)
const formClasses: Class[] = yearGroups.flatMap((yearGroup) =>
  formGroupCodes.map((code, idx) => {
    const formGroup = `${yearGroup}${code}`; // e.g., 7JW, 8FP
    const classPupils = mockPupils.filter((p) => p.classId === formGroup);
    const teacher = mockTeachers[idx] || mockTeachers[0];

    return {
      id: `class-${formGroup}`,
      name: formGroup,
      yearGroup,
      teacherId: teacher.id,
      pupilIds: classPupils.map((p) => p.id),
      capacity: 32,
    };
  }),
);

// Generate subject-specific classes
const subjectClasses: Class[] = [
  // Year 7 - English classes
  {
    id: 'ENG 7JW R101',
    name: '7JW',
    yearGroup: 7 as YearGroup,
    subjectId: 'subj-eng',
    teacherId: mockTeachers.find((t) => t.subjects.includes('ENG'))?.id || mockTeachers[0].id,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === 7)
      .slice(0, 28)
      .map((p) => p.id),
    capacity: 30,
  },
  // Year 7 - Maths classes
  {
    id: 'MAT 7FP R102',
    name: '7FP',
    yearGroup: 7 as YearGroup,
    subjectId: 'subj-mat',
    teacherId: mockTeachers.find((t) => t.subjects.includes('MAT'))?.id || mockTeachers[1].id,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === 7)
      .slice(0, 30)
      .map((p) => p.id),
    capacity: 30,
  },
  // Year 7 - Science classes
  {
    id: 'SCI 7PH L201',
    name: '7PH',
    yearGroup: 7 as YearGroup,
    subjectId: 'subj-sci',
    teacherId: mockTeachers.find((t) => t.subjects.includes('SCI'))?.id || mockTeachers[2].id,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === 7)
      .slice(0, 28)
      .map((p) => p.id),
    capacity: 30,
  },
  // Year 7 - History classes
  {
    id: 'HIS 7SM R103',
    name: '7SM',
    yearGroup: 7 as YearGroup,
    subjectId: 'subj-his',
    teacherId: mockTeachers.find((t) => t.subjects.includes('HIS'))?.id || mockTeachers[3].id,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === 7)
      .slice(0, 30)
      .map((p) => p.id),
    capacity: 30,
  },
  // Year 7 - Geography classes
  {
    id: 'GEO 7KL R104',
    name: '7KL',
    yearGroup: 7 as YearGroup,
    subjectId: 'subj-geo',
    teacherId: mockTeachers.find((t) => t.subjects.includes('GEO'))?.id || mockTeachers[4].id,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === 7)
      .slice(0, 30)
      .map((p) => p.id),
    capacity: 30,
  },
  // Year 8 - English classes
  {
    id: 'ENG 8JW R105',
    name: '8JW',
    yearGroup: 8 as YearGroup,
    subjectId: 'subj-eng',
    teacherId: mockTeachers.find((t) => t.subjects.includes('ENG'))?.id || mockTeachers[0].id,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === 8)
      .slice(0, 28)
      .map((p) => p.id),
    capacity: 30,
  },
  // Year 8 - Maths classes
  {
    id: 'MAT 8FP R106',
    name: '8FP',
    yearGroup: 8 as YearGroup,
    subjectId: 'subj-mat',
    teacherId: mockTeachers.find((t) => t.subjects.includes('MAT'))?.id || mockTeachers[1].id,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === 8)
      .slice(0, 30)
      .map((p) => p.id),
    capacity: 30,
  },
  // Year 8 - Science classes
  {
    id: 'SCI 8PH L202',
    name: '8PH',
    yearGroup: 8 as YearGroup,
    subjectId: 'subj-sci',
    teacherId: mockTeachers.find((t) => t.subjects.includes('SCI'))?.id || mockTeachers[2].id,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === 8)
      .slice(0, 25)
      .map((p) => p.id),
    capacity: 28,
  },
  // Year 9 - English classes
  {
    id: 'ENG 9SM R107',
    name: '9SM',
    yearGroup: 9 as YearGroup,
    subjectId: 'subj-eng',
    teacherId: mockTeachers.find((t) => t.subjects.includes('ENG'))?.id || mockTeachers[0].id,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === 9)
      .slice(0, 28)
      .map((p) => p.id),
    capacity: 30,
  },
  // Year 9 - Maths classes
  {
    id: 'MAT 9KL R108',
    name: '9KL',
    yearGroup: 9 as YearGroup,
    subjectId: 'subj-mat',
    teacherId: mockTeachers.find((t) => t.subjects.includes('MAT'))?.id || mockTeachers[1].id,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === 9)
      .slice(0, 30)
      .map((p) => p.id),
    capacity: 30,
  },
  // Year 9 - Science classes
  {
    id: 'SCI 9JW L203',
    name: '9JW',
    yearGroup: 9 as YearGroup,
    subjectId: 'subj-sci',
    teacherId: mockTeachers.find((t) => t.subjects.includes('SCI'))?.id || mockTeachers[2].id,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === 9)
      .slice(0, 28)
      .map((p) => p.id),
    capacity: 30,
  },
  // Year 9 - History classes
  {
    id: 'HIS 9FP R109',
    name: '9FP',
    yearGroup: 9 as YearGroup,
    subjectId: 'subj-his',
    teacherId: mockTeachers.find((t) => t.subjects.includes('HIS'))?.id || mockTeachers[3].id,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === 9)
      .slice(0, 26)
      .map((p) => p.id),
    capacity: 30,
  },
  // Year 10 - English classes
  {
    id: 'ENG 10PH R110',
    name: '10PH',
    yearGroup: 10 as YearGroup,
    subjectId: 'subj-eng',
    teacherId: mockTeachers.find((t) => t.subjects.includes('ENG'))?.id || mockTeachers[0].id,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === 10)
      .slice(0, 28)
      .map((p) => p.id),
    capacity: 30,
  },
  // Year 10 - Maths classes
  {
    id: 'MAT 10SM R111',
    name: '10SM',
    yearGroup: 10 as YearGroup,
    subjectId: 'subj-mat',
    teacherId: mockTeachers.find((t) => t.subjects.includes('MAT'))?.id || mockTeachers[1].id,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === 10)
      .slice(0, 30)
      .map((p) => p.id),
    capacity: 30,
  },
  // Year 10 - Physics classes
  {
    id: 'PHY 10KL L204',
    name: '10KL',
    yearGroup: 10 as YearGroup,
    subjectId: 'subj-phy',
    teacherId: mockTeachers.find((t) => t.subjects.includes('PHY'))?.id || mockTeachers[4].id,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === 10)
      .slice(0, 24)
      .map((p) => p.id),
    roomId: 'room-sci-1',
    capacity: 24,
  },
  // Year 10 - Chemistry classes
  {
    id: 'CHE 10JW L205',
    name: '10JW',
    yearGroup: 10 as YearGroup,
    subjectId: 'subj-che',
    teacherId: mockTeachers.find((t) => t.subjects.includes('CHE'))?.id || mockTeachers[5].id,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === 10)
      .slice(30, 52)
      .map((p) => p.id),
    roomId: 'room-sci-2',
    capacity: 24,
  },
  // Year 11 - English classes
  {
    id: 'ENG 11FP R112',
    name: '11FP',
    yearGroup: 11 as YearGroup,
    subjectId: 'subj-eng',
    teacherId: mockTeachers.find((t) => t.subjects.includes('ENG'))?.id || mockTeachers[0].id,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === 11)
      .slice(0, 28)
      .map((p) => p.id),
    capacity: 30,
  },
  // Year 11 - Maths classes
  {
    id: 'MAT 11PH R113',
    name: '11PH',
    yearGroup: 11 as YearGroup,
    subjectId: 'subj-mat',
    teacherId: mockTeachers.find((t) => t.subjects.includes('MAT'))?.id || mockTeachers[1].id,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === 11)
      .slice(0, 30)
      .map((p) => p.id),
    capacity: 30,
  },
  // Year 11 - Biology classes
  {
    id: 'BIO 11SM L206',
    name: '11SM',
    yearGroup: 11 as YearGroup,
    subjectId: 'subj-bio',
    teacherId: mockTeachers.find((t) => t.subjects.includes('BIO'))?.id || mockTeachers[6].id,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === 11)
      .slice(0, 26)
      .map((p) => p.id),
    roomId: 'room-sci-3',
    capacity: 26,
  },
  // Year 11 - Art classes
  {
    id: 'ART 11KL A101',
    name: '11KL',
    yearGroup: 11 as YearGroup,
    subjectId: 'subj-art',
    teacherId: mockTeachers.find((t) => t.subjects.includes('ART'))?.id || mockTeachers[7].id,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === 11)
      .slice(30, 48)
      .map((p) => p.id),
    roomId: 'room-art-1',
    capacity: 20,
  },
  // Year 9 - French classes
  {
    id: 'FRE 9PH R114',
    name: '9PH',
    yearGroup: 9 as YearGroup,
    subjectId: 'subj-fre',
    teacherId: mockTeachers.find((t) => t.subjects.includes('FRE'))?.id || mockTeachers[8].id,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === 9)
      .slice(30, 56)
      .map((p) => p.id),
    capacity: 28,
  },
  // Year 10 - Computer Science classes
  {
    id: 'ICT 10FP C101',
    name: '10FP',
    yearGroup: 10 as YearGroup,
    subjectId: 'subj-ict',
    teacherId: mockTeachers.find((t) => t.subjects.includes('ICT'))?.id || mockTeachers[9].id,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === 10)
      .slice(60, 84)
      .map((p) => p.id),
    roomId: 'room-comp-1',
    capacity: 25,
  },
];

// Combine all classes (subject classes first so they show up in tables)
export const mockClasses: Class[] = [...subjectClasses, ...formClasses];

// Hollins School set naming: Year + Class Letter + Subject Code (e.g., 7H Ma)
// Class letters spell HOLINS: H, O, L, I, N, S
const _classLetters = ['H', 'O', 'L', 'I', 'N', 'S'];

// Generate ability sets for Maths and English using Hollins naming
export const mockSets: ClassSet[] = yearGroups.flatMap((yearGroup) => [
  // Maths sets (using HOLINS letters)
  {
    id: `set-${yearGroup}-mat-H`,
    name: `${yearGroup}H Ma`,
    subjectId: 'subj-mat',
    yearGroup,
    level: 'higher' as const,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === yearGroup)
      .slice(0, 30)
      .map((p) => p.id),
    teacherId: mockTeachers.find((t) => t.subjects.includes('MAT'))?.id || mockTeachers[0].id,
  },
  {
    id: `set-${yearGroup}-mat-O`,
    name: `${yearGroup}O Ma`,
    subjectId: 'subj-mat',
    yearGroup,
    level: 'higher' as const,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === yearGroup)
      .slice(30, 60)
      .map((p) => p.id),
    teacherId: mockTeachers.find((t) => t.subjects.includes('MAT'))?.id || mockTeachers[1].id,
  },
  {
    id: `set-${yearGroup}-mat-L`,
    name: `${yearGroup}L Ma`,
    subjectId: 'subj-mat',
    yearGroup,
    level: 'foundation' as const,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === yearGroup)
      .slice(60, 90)
      .map((p) => p.id),
    teacherId: mockTeachers.find((t) => t.subjects.includes('MAT'))?.id || mockTeachers[2].id,
  },
  {
    id: `set-${yearGroup}-mat-I`,
    name: `${yearGroup}I Ma`,
    subjectId: 'subj-mat',
    yearGroup,
    level: 'foundation' as const,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === yearGroup)
      .slice(90, 120)
      .map((p) => p.id),
    teacherId: mockTeachers.find((t) => t.subjects.includes('MAT'))?.id || mockTeachers[3].id,
  },
  {
    id: `set-${yearGroup}-mat-N`,
    name: `${yearGroup}N Ma`,
    subjectId: 'subj-mat',
    yearGroup,
    level: 'foundation' as const,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === yearGroup)
      .slice(120, 150)
      .map((p) => p.id),
    teacherId: mockTeachers.find((t) => t.subjects.includes('MAT'))?.id || mockTeachers[4].id,
  },
  {
    id: `set-${yearGroup}-mat-S`,
    name: `${yearGroup}S Ma`,
    subjectId: 'subj-mat',
    yearGroup,
    level: 'lower' as const,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === yearGroup)
      .slice(150, 180)
      .map((p) => p.id),
    teacherId: mockTeachers.find((t) => t.subjects.includes('MAT'))?.id || mockTeachers[5].id,
  },
  // English sets (using HOLINS letters)
  {
    id: `set-${yearGroup}-eng-H`,
    name: `${yearGroup}H En`,
    subjectId: 'subj-eng',
    yearGroup,
    level: 'higher' as const,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === yearGroup)
      .slice(0, 30)
      .map((p) => p.id),
    teacherId: mockTeachers.find((t) => t.subjects.includes('ENG'))?.id || mockTeachers[5].id,
  },
  {
    id: `set-${yearGroup}-eng-O`,
    name: `${yearGroup}O En`,
    subjectId: 'subj-eng',
    yearGroup,
    level: 'higher' as const,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === yearGroup)
      .slice(30, 60)
      .map((p) => p.id),
    teacherId: mockTeachers.find((t) => t.subjects.includes('ENG'))?.id || mockTeachers[6].id,
  },
  {
    id: `set-${yearGroup}-eng-L`,
    name: `${yearGroup}L En`,
    subjectId: 'subj-eng',
    yearGroup,
    level: 'mixed' as const,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === yearGroup)
      .slice(60, 90)
      .map((p) => p.id),
    teacherId: mockTeachers.find((t) => t.subjects.includes('ENG'))?.id || mockTeachers[7].id,
  },
  {
    id: `set-${yearGroup}-eng-I`,
    name: `${yearGroup}I En`,
    subjectId: 'subj-eng',
    yearGroup,
    level: 'mixed' as const,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === yearGroup)
      .slice(90, 120)
      .map((p) => p.id),
    teacherId: mockTeachers.find((t) => t.subjects.includes('ENG'))?.id || mockTeachers[8].id,
  },
  {
    id: `set-${yearGroup}-eng-N`,
    name: `${yearGroup}N En`,
    subjectId: 'subj-eng',
    yearGroup,
    level: 'foundation' as const,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === yearGroup)
      .slice(120, 150)
      .map((p) => p.id),
    teacherId: mockTeachers.find((t) => t.subjects.includes('ENG'))?.id || mockTeachers[9].id,
  },
  {
    id: `set-${yearGroup}-eng-S`,
    name: `${yearGroup}S En`,
    subjectId: 'subj-eng',
    yearGroup,
    level: 'foundation' as const,
    pupilIds: mockPupils
      .filter((p) => p.yearGroup === yearGroup)
      .slice(150, 180)
      .map((p) => p.id),
    teacherId: mockTeachers.find((t) => t.subjects.includes('ENG'))?.id || mockTeachers[0].id,
  },
]);

// Generate bands
export const mockBands: Band[] = yearGroups
  .map((yearGroup) => ({
    id: `band-${yearGroup}-upper`,
    name: 'Upper Band',
    yearGroup,
    classIds: [`class-${yearGroup}JW`, `class-${yearGroup}FP`, `class-${yearGroup}PH`],
    description: 'Upper band form groups - Higher attaining pupils',
  }))
  .concat(
    yearGroups.map((yearGroup) => ({
      id: `band-${yearGroup}-lower`,
      name: 'Lower Band',
      yearGroup,
      classIds: [`class-${yearGroup}SM`, `class-${yearGroup}KL`],
      description: 'Lower band form groups - Foundation level pupils',
    })),
  );

export default mockClasses;
